import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Modal } from '@/shared/ui';

import { MFACredentialAction } from '../../config';
import { useMFAEnrollment } from '../../hooks';
import { MFAEnrollmentActions } from '../MFAEnrollmentActions/MFAEnrollmentActions';
import { MFASetupPanel } from '../MFASetupPanel/MFASetupPanel';
import { RecoveryCodesPanel } from '../RecoveryCodesPanel/RecoveryCodesPanel';

import styles from './MFAEnrollmentModal.module.scss';

type MFAEnrollmentModalProps = {
  title: string;
  cancelLabel: ReactNode;
  className?: string;
  description?: ReactNode;
  collapsibleSetupKey?: boolean;
  action?: MFACredentialAction;
  onCancel: () => void;
  onCompleted: () => void;
  onSignInRequired: () => void;
};

/**
 * Enrolment inside the shared modal chrome: registration and personal settings
 * reach setup from a page that stays behind it.
 *
 * It has no `isOpen`: mounting starts an enrolment, and every Begin replaces
 * the pending secret server-side, so the caller mounts it conditionally.
 */
export const MFAEnrollmentModal: FC<MFAEnrollmentModalProps> = (props) => {
  const {
    title,
    cancelLabel,
    className,
    description,
    collapsibleSetupKey,
    action = 'enroll',
    onCancel,
    onCompleted,
    onSignInRequired,
  } = props;

  const {
    secret,
    otpauthURI,
    code,
    errorMessage,
    recoveryCodes,
    isStarting,
    isConfirming,
    isCodeComplete,
    isCurrentCodeRequired,
    signInRequired,
    setCode,
    confirm,
  } = useMFAEnrollment(action);

  const hasRecoveryCodes = recoveryCodes.length > 0;

  return (
    <Modal
      isOpen
      className={className}
      title={title}
      dataSection="mfa-enrollment-modal"
      shouldRenderCloseIconButton={false}
      shouldCloseOnOverlayClick={false}
      hideFooter={hasRecoveryCodes}
      footer={
        <MFAEnrollmentActions
          className={styles.footer}
          cancelLabel={cancelLabel}
          isStarting={isStarting}
          isConfirming={isConfirming}
          isCodeComplete={isCodeComplete}
          onCancel={onCancel}
          onConfirm={() => confirm(code)}
        />
      }
    >
      {hasRecoveryCodes ? (
        <RecoveryCodesPanel
          codes={recoveryCodes}
          signInRequired={signInRequired}
          onContinue={signInRequired ? onSignInRequired : onCompleted}
        />
      ) : (
        <MFASetupPanel
          secret={secret}
          otpauthURI={otpauthURI}
          code={code}
          isLoading={isStarting}
          disabled={isConfirming}
          errorMessage={errorMessage}
          description={description}
          collapsibleSetupKey={collapsibleSetupKey}
          currentCodeDescription={
            isCurrentCodeRequired && (
              <p className="p2">
                <FormattedMessage
                  id="mfa.setup.currentCodeDescription"
                  defaultMessage="Before replacing your authenticator, enter a current code from the app you already use."
                />
              </p>
            )
          }
          onCodeChange={setCode}
          onCodeComplete={confirm}
        />
      )}
    </Modal>
  );
};
